<template>
  <div class="activity-log-panel">
    <div class="panel-header">
      <h2 class="panel-title">Log de atividades</h2>
      <div class="panel-filters">
        <select v-model="filterEntity" class="filter-select" @change="load">
          <option value="">Todos</option>
          <option value="task">Tarefas</option>
          <option value="project">Projetos</option>
          <option value="account">Usuários</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="log-loading">
      <div class="spinner" />
      Carregando…
    </div>

    <div v-else-if="entries.length === 0" class="log-empty">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><rect x="6" y="4" width="28" height="32" rx="3" stroke="var(--ink-muted,#888)" stroke-width="1.8"/><path d="M13 14h14M13 20h10M13 26h7" stroke="var(--ink-muted,#888)" stroke-width="1.8" stroke-linecap="round"/></svg>
      <p>Nenhuma atividade registrada ainda.</p>
    </div>

    <div v-else class="log-list">
      <div v-for="entry in entries" :key="entry.id" class="log-entry">
        <div class="log-avatar" :style="{ background: avatarColor(entry.actorName) }">
          {{ initials(entry.actorName) }}
        </div>
        <div class="log-body">
          <div class="log-main">
            <span class="log-actor">{{ entry.actorName }}</span>
            <span class="log-action-chip" :class="'action-' + actionClass(entry.action)">{{ actionLabel(entry.action) }}</span>
            <span v-if="entry.entityName" class="log-entity">{{ entry.entityName }}</span>
          </div>
          <div class="log-meta">
            <span v-if="entry.details" class="log-details">{{ entry.details }}</span>
            <span class="log-time">{{ formatTime(entry.createdAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="entries.length > 0 && !loading" class="log-footer">
      <button class="load-more-btn" @click="loadMore">Carregar mais</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { apiFetch } from '@/store/api'

const COLORS = ['#2563eb','#7c3aed','#0891b2','#059669','#d97706','#dc2626','#db2777']

const ACTION_LABELS: Record<string, string> = {
  'task-created': 'criou tarefa',
  'task-updated': 'editou tarefa',
  'progress-update': 'atualizou progresso',
  'task-comment': 'comentou em',
  'task-deleted': 'removeu tarefa',
  'project-created': 'criou projeto',
  'project-updated': 'editou projeto',
  'user-created': 'criou usuário',
  'user-updated': 'editou usuário',
}

export default defineComponent({
  name: 'ActivityLogPanel',
  setup() {
    const entries = ref<any[]>([])
    const loading = ref(false)
    const filterEntity = ref('')
    const limit = ref(50)

    const load = async () => {
      loading.value = true
      try {
        const params = new URLSearchParams({ limit: String(limit.value) })
        if (filterEntity.value) params.set('entityType', filterEntity.value)
        const data = await apiFetch(`/activity-log?${params}`)
        entries.value = data.log || []
      } catch { entries.value = [] }
      loading.value = false
    }

    const loadMore = () => { limit.value += 50; load() }

    onMounted(load)

    const initials = (name: string) => {
      const p = name.trim().split(/\s+/)
      return p.length >= 2 ? (p[0][0] + p[p.length-1][0]).toUpperCase() : name.slice(0,2).toUpperCase()
    }
    const avatarColor = (name: string) => COLORS[(name.charCodeAt(0)||0) % COLORS.length]
    const actionLabel = (action: string) => ACTION_LABELS[action] || action
    const actionClass = (action: string) => {
      if (action.includes('created')) return 'green'
      if (action.includes('deleted') || action.includes('removed')) return 'red'
      if (action.includes('comment')) return 'blue'
      return 'gray'
    }
    const formatTime = (iso: string) => {
      if (!iso) return ''
      const d = new Date(iso)
      const now = new Date()
      const diffMs = now.getTime() - d.getTime()
      const diffMin = Math.floor(diffMs / 60000)
      if (diffMin < 1) return 'agora'
      if (diffMin < 60) return `${diffMin}min atrás`
      const diffH = Math.floor(diffMin / 60)
      if (diffH < 24) return `${diffH}h atrás`
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    }

    return { entries, loading, filterEntity, load, loadMore, initials, avatarColor, actionLabel, actionClass, formatTime }
  }
})
</script>

<style scoped lang="scss">
.activity-log-panel {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 860px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.panel-title { margin: 0; font-size: 18px; font-weight: 700; color: var(--ink, #1f1e1a); }

.filter-select {
  padding: 6px 10px;
  border: 1.5px solid var(--border, rgba(0,0,0,.12));
  border-radius: 8px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  cursor: pointer;
}

.log-loading, .log-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: var(--ink-muted, #888);
  font-size: 14px;
  svg { opacity: .5; }
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2.5px solid var(--border, rgba(0,0,0,.1));
  border-top-color: var(--accent, #2563eb);
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.log-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.log-entry {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background .1s;
  &:hover { background: var(--surface, rgba(0,0,0,.03)); }
}

.log-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.log-body { flex: 1; min-width: 0; }

.log-main {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.log-actor { font-size: 13px; font-weight: 600; color: var(--ink, #1f1e1a); }

.log-action-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 99px;
  &.action-green { background: #dcfce7; color: #15803d; }
  &.action-red { background: #fee2e2; color: #b91c1c; }
  &.action-blue { background: #dbeafe; color: #1d4ed8; }
  &.action-gray { background: var(--surface-3, rgba(0,0,0,.06)); color: var(--ink-muted, #888); }
}

.log-entity {
  font-size: 13px;
  color: var(--ink, #1f1e1a);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
}

.log-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}
.log-details {
  font-size: 11px;
  color: var(--ink-muted, #888);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}
.log-time {
  font-size: 11px;
  color: var(--ink-muted, #888);
  white-space: nowrap;
  flex-shrink: 0;
}

.log-footer {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}
.load-more-btn {
  padding: 8px 20px;
  border: 1.5px solid var(--border, rgba(0,0,0,.12));
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  color: var(--ink-muted, #888);
  cursor: pointer;
  &:hover { border-color: var(--accent, #2563eb); color: var(--accent, #2563eb); }
}
</style>
